import type { Address } from '$types/address';
import type { User } from '$types/user';
import type { AvatarType } from '$types/avatarType';
import type { AvatarBalanceFromEnvio, CirclesAvatarFromEnvio } from '$types/envioTypes';
import { executeQuery } from '../executeQuery';
import { getAddress } from 'viem';
import { AvatarQueries } from './avatarQueries';
import { applyDemurrageOnBalance } from '$services/circlesV2Utils/tokens';
import { users } from '$lib/states/users.svelte';
import { truncateAddress } from '$utils';
import { mapAvatarTypeToUserType } from '$types/userType';
import { getImageFromCidV0 } from '$utils/getImageFromCid';
import { getMetriAddress } from '$utils/getMetriAddress';
import { searchRankingResult } from '$services/circlesV2Utils/searchRanking';
import { WHITELIST_CONTROLLER_ADDRESS } from '$services/circlesV2Utils/envio/groups/fetchGroups';
import { mapTrustRelationFromOpposite } from '$services/circlesV2Utils/mapTrustRelationFromOpposite';
import { mapSDKTrustToMetriTrust } from '$utils/mapSDKTrustToMetriTrust';
import type { WrappedV2Object } from '$types/circlesBreakdownItem';

/**
 * Finds profiles by name search
 */
export const findProfilesByName = async (
  queryString: string,
  avatarTypes: AvatarType[],
  viewerAddress: Address = getMetriAddress(),
): Promise<User[]> => {
  const query = `%${queryString}%`;

  const data = await executeQuery<{
    Avatar: CirclesAvatarFromEnvio[];
  }>(
    AvatarQueries.FIND_PROFILES_BY_USERNAME,
    {
      query,
      avatarTypes,
      whitelistAddress: WHITELIST_CONTROLLER_ADDRESS,
      viewerAddress,
    },
    'findByUserName',
  );

  const potentialUsers = await Promise.all(
    data.Avatar.filter((row) => !!row.profile).map(async (row) =>
      users.byAddress(row.id, row).then((e) => {
        return {
          ...e.current,
          trust: mapSDKTrustToMetriTrust(
            mapTrustRelationFromOpposite(
              row.trustGivenToViewer ?? [],
              row.trustReceivedFromViewer ?? [],
            ),
            row.id!,
          ),
        };
      }),
    ),
  );

  const rankedUsers = searchRankingResult(potentialUsers, queryString);

  return rankedUsers;
};

/**
 * Gets profile information for an address (batched)
 */
export const getProfileForAddress = async (address: Address): Promise<CirclesAvatarFromEnvio> => {
  const normalizedAddress = getAddress(address);

  const query = AvatarQueries.PROFILE_FOR_ADDRESS;

  const res = await executeQuery<{ Avatar: CirclesAvatarFromEnvio[] }>(
    query,
    { address: normalizedAddress },
    'getProfileForAddressQuery',
  );

  return res.Avatar[0];
};

/**
 * Gets profiles waiting for invites with limit
 */
export const getProfilesAwaitingInvites = async (
  offset = 0,
  limit = 200,
  truster: Address = getMetriAddress(),
): Promise<CirclesAvatarFromEnvio[]> => {
  const data = await executeQuery<{ Avatar: CirclesAvatarFromEnvio[] }>(
    AvatarQueries.ALL_USERS_WAITING_INVITES_WITH_COMPLETE_V2_PROFILES,
    { limit, offset, truster },
    'getProfilesAwaitingInvites',
  );

  return Promise.all(
    data.Avatar.map(async (avatar) => {
      if (avatar.profile) {
        avatar.profile.picture = await getImageFromCidV0(avatar.profile.cidV0);
      }
      return avatar;
    }),
  );
};

/**
 * Gets CIDV0 for an address
 */
export const getCidV0ForAddress = async (address: Address): Promise<string | undefined> => {
  const data = await executeQuery<{ Avatar: CirclesAvatarFromEnvio[] }>(
    AvatarQueries.CIDV0_FOR_ADDRESS,
    { address: getAddress(address) },
    'getCidV0ForAddressQuery',
  );
  const avatar = data.Avatar[0];
  return avatar?.profile?.cidV0;
};

/**
 * Gets all CRC tokens for a user
 */
export const getUserAllCrcTokens = async (
  address: Address,
): Promise<{
  AvatarBalance: AvatarBalanceFromEnvio[];
  Avatar: CirclesAvatarFromEnvio[];
}> => {
  const data = await executeQuery<{
    AvatarBalance: AvatarBalanceFromEnvio[];
    Avatar: CirclesAvatarFromEnvio[];
  }>(AvatarQueries.GET_USER_ALL_CRC_TOKENS, { address }, 'getUserAllCrcTokens');

  return data;
};

export const getOwnCrcTokens = async (address: Address, wrappedOnly = false) => {
  const data = await executeQuery<{
    AvatarBalance: AvatarBalanceFromEnvio[];
  }>(AvatarQueries.GET_OWN_CRC_TOKENS, { address, wrappedOnly }, 'getOwnCrcTokens');

  return {
    data,
    get balance() {
      return data.AvatarBalance.reduce(
        (acc, balanceObj) => acc + applyDemurrageOnBalance(balanceObj),
        0,
      );
    },
    get addresses() {
      return data.AvatarBalance.map((balanceObj) => balanceObj.token.id as Address);
    },
    get wrappedV2Object() {
      const result: WrappedV2Object = {
        inflationaryBalance: 0,
        demurrageBalance: 0,
      };
      for (const balanceObj of data.AvatarBalance) {
        if (balanceObj.token.tokenType === 'WrappedDemurrageToken') {
          result.demurrageBalance = applyDemurrageOnBalance(balanceObj);
          result.demurrageAddress = balanceObj.token.id;
        } else if (balanceObj.token.tokenType === 'WrappedStaticToken') {
          result.inflationaryBalance = applyDemurrageOnBalance(balanceObj);
          result.inflationaryAddress = balanceObj.token.id;
        }
      }
      return result;
    },
  };
};

export const getAgGroupCrcBalance = async (
  address: Address,
  tokenIds: string[],
): Promise<number> => {
  const data = await executeQuery<{
    AvatarBalance: AvatarBalanceFromEnvio[];
  }>(AvatarQueries.GET_AG_GROUP_CRC_BALANCE, { address, tokenIds }, 'getAgGroupCrcBalance');

  return data.AvatarBalance.reduce(
    (acc, balanceObj) => acc + applyDemurrageOnBalance(balanceObj),
    0,
  );
};

/**
 * Checks if an address is inviteable
 */
export const checkIfInviteableAddress = async (
  address: Address,
): Promise<{
  hasCirclesProfile: boolean;
  isAlreadyV2: boolean;
}> => {
  const data = await executeQuery<{ Avatar: CirclesAvatarFromEnvio[] }>(
    AvatarQueries.PROFILE_FOR_ADDRESS,
    { address },
    'getProfileForAddressQuery',
  );

  const avatarType = data.Avatar[0]?.avatarType as AvatarType;

  const isAlreadyV2 =
    avatarType === 'RegisterHuman' ||
    avatarType === 'RegisterGroup' ||
    avatarType === 'RegisterOrganization';

  return {
    hasCirclesProfile: data.Avatar.length > 0,
    isAlreadyV2,
  };
};

export const getProfileForAddresses = async (addresses: Address[]): Promise<User[]> => {
  const data = await executeQuery<{ Avatar: CirclesAvatarFromEnvio[] }>(
    AvatarQueries.PROFILE_FOR_ADDRESSES,
    { addresses },
    'getProfileForAddresses',
  );

  return Promise.all(
    data.Avatar.map(async (avatar) => {
      const picture = avatar.profile?.cidV0
        ? await getImageFromCidV0(avatar.profile?.cidV0)
        : undefined;
      return {
        address: avatar.id,
        name: avatar.profile?.name ?? truncateAddress(avatar.id),
        cidV0: avatar.profile?.cidV0,
        verificationBadge: avatar.verificationBadge,
        type: mapAvatarTypeToUserType(avatar.avatarType),
        picture,
      } as User;
    }),
  );
};
